import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { esErrorNoReintentable } from './lib/http';
import { inicializarI18n } from './lib/i18n';
import { routeTree } from './routeTree.gen';
import './styles/index.css';

// Tope de reintentos para fallos transitorios (red caída, 5xx): el valor por defecto de TanStack
// Query (3, con backoff 1s/2s/4s) multiplica por 4 cada petición fallida, y contra un backend que ya
// limita peticiones eso convierte un solo 429 en una ráfaga de 4 contra un endpoint ya bloqueado. Con
// 1 reintento basta para absorber un parpadeo de red sin insistir contra un límite que no va a ceder.
const TOPE_REINTENTOS_TRANSITORIOS = 1;

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			// Un 4xx (429 incluido) no se arregla reintentando: `esErrorNoReintentable` es la misma
			// lógica que ya usan los hooks con retry a medida (`http.ts`, oleada 1). Para el resto
			// (fallo de red, 5xx) se reintenta hasta el tope de arriba.
			retry: (intentoFallido, error) => !esErrorNoReintentable(error) && intentoFallido < TOPE_REINTENTOS_TRANSITORIOS,
			staleTime: 30_000,
		},
	},
});

// Sin `context`, `beforeLoad` no tiene `queryClient` con el que comprobar la sesión: no habría
// guards de ruta (`_autenticado.tsx`, `_autenticado/centro.tsx`).
const router = createRouter({ context: { queryClient }, routeTree });

declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router;
	}
}

const contenedor = document.getElementById('root');
if (!contenedor) {
	throw new Error('No se encontró el nodo #root en index.html');
}

// i18n se inicializa antes del primer render para que no se vea un parpadeo sin traducir.
await inicializarI18n();

createRoot(contenedor).render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
			<RouterProvider router={router} />
		</QueryClientProvider>
	</StrictMode>,
);
